
import {
  ref,
  computed,
  defineComponent,
  useContext,
  useFetch,
  useMeta,
  useRoute,
  watch,
} from '@nuxtjs/composition-api';
import {
  useBreadcrumbs,
  useProductPage,
  usePagePreloader,
} from '~/composables';
import ProductSeries from '~/components/sections/productSection/ProductSeries.vue';
import ProductDetails from '~/components/sections/productSection/ProductDetails.vue';
import RelatedProducts from '~/components/sections/productSection/RelatedProducts.vue';
import CategoryRecommended from '~/components/sections/categorySection/CategoryRecommended.vue';
import ConsultationForm from '~/components/sections/formSection/ConsultationForm.vue';

export default defineComponent({
  components: {
    RelatedProducts,
    ConsultationForm,
    ProductDetails,
    ProductSeries,
    CategoryRecommended,
  },
  setup() {
    const { error, ssrContext } = useContext();
    const route = useRoute();
    const pagePreloader = usePagePreloader();
    const currentPage = computed(() => route.value.query.page);
    const { breadcrumbs, addCrumbs } = useBreadcrumbs();
    const { product, productContent, fetchProduct } = useProductPage();
    const categoryRecommendedData = ref();

    const fetchProductPage = async () => {
      try {
        pagePreloader.start();

        await fetchProduct({
          slug: route.value.params.slug,
        });

        const productCategories = product.value.category_branch.length
          ? product.value.category_branch.reduceRight((categories, current) => {
              return [
                ...categories,
                {
                  text: current.name,
                  link: `/catalog/${current.slug}`,
                },
              ];
            }, [])
          : [];
        addCrumbs('catalog', ...productCategories, product.value.name);
        categoryRecommendedData.value = product.value.content.find(
          (block) => block.type === 'category-recommended',
        )?.data;
      } catch (e) {
        if (ssrContext) ssrContext.res.statusCode = 404;
        else error({ statusCode: 404 });
      } finally {
        pagePreloader.stop();
      }
    };

    const relatedProducts = computed(
      () => product?.value?.recommended_products,
    );

    useFetch(async () => {
      await fetchProductPage();
    });

    watch(currentPage, () => {
      fetchProductPage();
    });

    useMeta(() => ({
      title: product.value?.seo?.page_title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: product.value?.seo?.description,
        },
        {
          property: 'og:title',
          content: product.value?.seo?.og_title,
        },
      ],
    }));

    return {
      pagePreloader,
      breadcrumbs,
      product,
      productContent,
      categoryRecommendedData,
      relatedProducts,
    };
  },
  head: {},
});
